interface Sentence {
  original: string;
  french: string;
  // English translation of `french`, optional; falls back to `french` when absent.
  english?: string;
  words: string[];
  audio?: string;
  category: string;
  tags: string[];
}

export const SENTENCE_CATEGORIES = {
  'Salutations': {
    icon: '👋',
    description: 'Premiers contacts et formules de politesse',
  },
  'Présentation': {
    icon: '🤝',
    description: 'Se présenter et apprendre les démonstratifs',
  },
  'Vie quotidienne': {
    icon: '🌞',
    description: 'Expressions du quotidien et présent démonstratif',
  },
  'Au marché': {
    icon: '🛍️',
    description: 'Faire ses courses et négocier',
  },
  'Voyages': {
    icon: '✈️',
    description: 'Se déplacer et visiter',
  },
  'Famille et relations': {
    icon: '👨‍👩‍👧‍👦',
    description: 'Parler de sa famille et apprendre les pluriels',
  },
  'Urgences et santé': {
    icon: '🏥',
    description: 'Situations d\'urgence et santé',
  },
  'Loisirs et culture': {
    icon: '🎭',
    description: 'Activités culturelles et divertissement',
  },
} as const;

export const sentencesToConstruct: Sentence[] = [
  // Phrases démonstratives - intégrées dans "Vie quotidienne"
  {
    original: "Mangi fii rekk",
    french: "Je suis ici",
    english: "I am here",
    words: ["Mangi", "fii","rekk"],
    audio: "Mangi_fi_rekk",
    category: 'Vie quotidienne',
    tags: ['démonstratif', 'présence', 'localisation', 'grammaire']
  },
  {
    original: "Yangi toog",
    french: "Tu es assis",
    english: "You are sitting",
    words: ["Yangi", "toog"],
    category: 'Vie quotidienne',
    tags: ['démonstratif', 'position', 'présent', 'grammaire']
  },
  {
    original: "Mungi dem",
    french: "Il/Elle est en train de partir",
    english: "He/She is leaving",
    words: ["Mungi", "dem"],
    audio: "Mungi_dem",
    category: 'Vie quotidienne',
    tags: ['démonstratif', 'mouvement', 'départ', 'grammaire']
  },
  {
    original: "Ñungi toog ci kër gi",
    french: "Nous sommes assis dans cette maison",
    english: "We are sitting in this house",
    words: ["Ñungi", "toog", "ci", "kër", "gi"],
    audio: "nungi_toog_ci_ker_gi",
    category: 'Vie quotidienne',
    tags: ['démonstratif', 'pluriel', 'localisation', 'grammaire']
  },
  {
    original: "Yéénangi xool tele",
    french: "Vous êtes en train de regarder",
    english: "You are watching",
    words: ["Yéénangi", "xool"],
    audio: "yeenangi_xool_tele",
    category: 'Vie quotidienne',
    tags: ['démonstratif', 'action', 'présent', 'grammaire']
  },
  {
    original: "Ñungë nellaw",
    french: "Ils/Elles dorment",
    english: "They are sleeping",
    words: ["Ñungë", "nellaw"],
    category: 'Vie quotidienne',
    tags: ['démonstratif', 'pluriel', 'sommeil', 'grammaire']
  },
  {
    original: "Taxi bangi dem",
    french: "Ce taxi-ci s'en va",
    english: "This taxi is leaving",
    words: ["Taxi", "bangi", "dem"],
    audio: "taxi_bangi_dem",
    category: 'Vie quotidienne',
    tags: ['démonstratif', 'transport', 'proximité', 'grammaire']
  },
  {
    original: "Ndawsa ngi toog",
    french: "Cette femme-là est assise",
    english: "That woman is sitting",
    words: ["Ndaxsa", "ngi", "toog"],
    audio: "Ndawsa_ngi_toog",
    category: 'Vie quotidienne',
    tags: ['démonstratif', 'éloignement', 'position', 'grammaire']
  },
  {
    original: "Goor gangiy liggéy",
    french: "Ce garçon-ci est en train de travailler",
    english: "This boy is working",
    words: ["Goor", "gangiy", "liggéy"],
    audio: "goor_gangiy_liggey",
    category: 'Vie quotidienne',
    tags: ['démonstratif', 'proximité', 'travail', 'grammaire']
  },
  {
    original: "Nit ñangiy lekk cééb bi",
    french: "Ces personnes sont en train de manger ce riz",
    english: "These people are eating this rice",
    words: ["Nit", "ñangiy", "lekk", "cééb", "bi"],
    audio: "nit_nangiy_lekk_ceeb_bi",
    category: 'Vie quotidienne',
    tags: ['démonstratif', 'pluriel', 'nourriture', 'grammaire']
  },

  // Phrases avec démonstratifs et distance - intégrées dans "Présentation"
  {
    original: "Xaj baangi fii",
    french: "Ce chien est ici",
    english: "This dog is here",
    words: ["Xaj", "bangi","fii"],
    category: 'Présentation',
    tags: ['démonstratif', 'proximité', 'animaux', 'grammaire']
  },
  {
    original: "Nag bi rey na",
    french: "Cette vache-là est grande",
    english: "That cow is big",
    words: ["Nag", "bi", "rey", "na"],
    audio: "nag_bi_rey_na",
    category: 'Présentation',
    tags: ['démonstratif', 'éloignement', 'animaux', 'grammaire']
  },
  {
    original: "Fas wi gaw na",
    french: "Ce chevalest grand",
    english: "This horse is big",
    words: ["Fas", "wi", "gaw", "na"],
    category: 'Présentation',
    tags: ['démonstratif', 'animaux', 'description', 'grammaire']
  },
  
  {
    original: "Kër boobu yombul",
    french: "Cette maison là-bas n'est pas bon marché",
    english: "That house over there is not cheap",
    words: ["Kër", "boobu", "yombul"],
    audio: "ker_boobu_yombul",
    category: 'Présentation',
    tags: ['démonstratif', 'distance', 'négation', 'grammaire']
  },
  {
    original: "Xar bëlé rey na lool",
    french: "Ce mouton là-bas au loin est très grand",
    english: "That sheep far away is very big",
    words: ["Xar", "bëlé", "rey", "na", "lool"],
    audio: "xar_bele_rey_na_lool",
    category: 'Présentation',
    tags: ['démonstratif', 'distance_lointaine', 'animal', 'grammaire']
  },

  // Phrases avec pluriels - intégrées dans "Famille et relations"
  {
    original: "Yaay yi dañuy jàng",
    french: "Ces mères étudient",
    english: "These mothers are studying",
    words: ["Yaay", "yi", "dañuy", "jàng"],
    audio: "yaay_yi_danuy_jang",
    category: 'Famille et relations',
    tags: ['pluriel', 'famille', 'action', 'grammaire']
  },
  {
    original: "Doom yi toog nañu",
    french: "Ces enfants sont assis",
    english: "These children are sitting",
    words: ["Doom", "yi", "toog", "nañu"],
    category: 'Famille et relations',
    tags: ['pluriel', 'famille', 'position', 'grammaire']
  },
  {
    original: "Fas yi rey nañu",
    french: "Ces chevaux-ci sont grands",
    english: "These horses are big",
    words: ["Fas", "yi", "rey", "nañu"],
    category: 'Famille et relations',
    tags: ['pluriel', 'animaux', 'description', 'grammaire']
  },
  {
    original: "Maam yi rafet nañu",
    french: "Ces grands-parents sont beaux",
    english: "These grandparents are beautiful",
    words: ["Maam", "yi", "rafet", "nañu"],
    category: 'Famille et relations',
    tags: ['pluriel', 'famille', 'beauté', 'grammaire']
  },
  {
    original: "Xaj yi dañuy daw",
    french: "Ces chiens sont en train de courir",
    english: "These dogs are running",
    words: ["Xaj", "yi", "dañuy", "daw"],
    category: 'Famille et relations',
    tags: ['pluriel', 'animaux', 'mouvement', 'grammaire']
  },
  {
    original: "Nag yi rey nañu",
    french: "Ces vaches sont grandes",
    english: "These cows are big",
    words: ["Nag", "yi", "rey", "nañu"],
    category: 'Famille et relations',
    tags: ['pluriel', 'animaux', 'description', 'grammaire']
  },
  {
    original: "Jiggéen yi dañuy tog",
    french: "Ces femmes sont en train de cuisiner",
    english: "These women are cooking",
    words: ["Jiggéen", "yi", "dañuy", "tog"],
    category: 'Famille et relations',
    tags: ['pluriel', 'famille', 'cuisine', 'grammaire']
  },
  {
    original: "Goor yi dañuy liggéy",
    french: "Ces hommes sont en train de travailler",
    english: "These men are working",
    words: ["Goor", "yi", "dañuy", "liggéy"],
    category: 'Famille et relations',
    tags: ['pluriel', 'famille', 'travail', 'grammaire']
  },
  {
    original: "Xar yi lekk nañu",
    french: "Ces moutons mangent",
    english: "These sheep are eating",
    words: ["Xar", "yi", "lekk", "nañu"],
    category: 'Famille et relations',
    tags: ['pluriel', 'animaux', 'nourriture', 'grammaire']
  },
  {
    original: "Doom yi bëgg nañu lekk",
    french: "Ces enfants veulent manger",
    english: "These children want to eat",
    words: ["Doom", "yi", "bëgg", "nañu", "lekk"],
    category: 'Famille et relations',
    tags: ['pluriel', 'famille', 'désir', 'grammaire']
  },
  {
    original: "Fas yi dem nañu",
    french: "Ces chevaux s'en vont",
    english: "These horses are leaving",
    words: ["Fas", "yi", "dem", "nañu"],
    category: 'Famille et relations',
    tags: ['pluriel', 'animaux', 'mouvement', 'grammaire']
  },
  {
    original: "Muus yi dañuy daw",
    french: "Ces chats sont en train de courir",
    english: "These cats are running",
    words: ["Muus", "yi", "dañuy", "daw"],
    category: 'Famille et relations',
    tags: ['pluriel', 'animaux', 'mouvement', 'grammaire']
  },

  // Ngi pluriel — Nu ngi / Ñu ngi (progressif pluriel)
  {
    original: "Sunu doom yi ñu ngi jàng",
    french: "Nos enfants sont en train d'étudier",
    english: "Our children are studying",
    words: ["Sunu", "doom", "yi", "ñu", "ngi", "jàng"],
    category: 'Famille et relations',
    tags: ['ngi-pluriel', 'famille', 'possessif', 'grammaire']
  },
  {
    original: "Seen xarit yi nu ngi dem",
    french: "Vos amis sont en train de partir",
    english: "Your friends are leaving",
    words: ["Seen", "xarit", "yi", "nu", "ngi", "dem"],
    category: 'Famille et relations',
    tags: ['ngi-pluriel', 'famille', 'possessif', 'grammaire']
  },
  {
    original: "Sunu maam yi nu ngi toog",
    french: "Nos grands-parents sont assis",
    english: "Our grandparents are sitting",
    words: ["Sunu", "maam", "yi", "nu", "ngi", "toog"],
    category: 'Famille et relations',
    tags: ['ngi-pluriel', 'famille', 'possessif', 'grammaire']
  },

  // Focus Da- pluriel — Danu / Dañu
  {
    original: "Danu am ñaar muus",
    french: "Nous avons deux chats",
    english: "We have two cats",
    words: ["Danu", "am", "ñaar", "muus"],
    category: 'Famille et relations',
    tags: ['focus-da', 'animaux', 'nombres', 'grammaire']
  },
  {
    original: "Dañu am bënn xaj",
    french: "Ils/Elles ont un chien",
    english: "They have a dog",
    words: ["Dañu", "am", "bënn", "xaj"],
    category: 'Famille et relations',
    tags: ['focus-da', 'animaux', 'nombres', 'grammaire']
  },
  {
    original: "Danu am ñett rakk",
    french: "Nous avons trois petits frères/sœurs",
    english: "We have three younger siblings",
    words: ["Danu", "am", "ñett", "rakk"],
    category: 'Famille et relations',
    tags: ['focus-da', 'famille', 'nombres', 'grammaire']
  },

  // Classe nominale G (gi) — animaux / noms particuliers
  {
    original: "Gaynde gi dafa rey",
    french: "Ce lion est grand",
    english: "This lion is big",
    words: ["Gaynde", "gi", "dafa", "rey"],
    category: 'Famille et relations',
    tags: ['classe-G', 'animaux', 'démonstratif', 'grammaire']
  },

  // Pronoms emphatiques pluriel — Nun / Yeen / Ñoom
  {
    original: "Nun ñoo am sama doom yi",
    french: "C'est nous qui avons mes enfants",
    english: "It is we who have my children",
    words: ["Nun", "ñoo", "am", "sama", "doom", "yi"],
    category: 'Famille et relations',
    tags: ['emphatique', 'famille', 'possessif', 'grammaire']
  },
  {
    original: "Ñoom ñoo am xaj yi",
    french: "Ce sont eux/elles qui ont les chiens",
    english: "It is they who have the dogs",
    words: ["Ñoom", "ñoo", "am", "xaj", "yi"],
    category: 'Famille et relations',
    tags: ['emphatique', 'animaux', 'pluriel', 'grammaire']
  },

  // Pronoms objets pluriel — leen / léen
  {
    original: "Dama leen bëgg sama doom yi",
    french: "J'aime mes enfants",
    english: "I love my children",
    words: ["Dama", "leen", "bëgg", "sama", "doom", "yi"],
    category: 'Famille et relations',
    tags: ['pronom-objet', 'famille', 'sentiment', 'grammaire']
  },

  // Salutations
  {
    original: "Nanga def ?",
    french: "Comment allez-vous ?",
    english: "How are you?",
    words: ["Nanga", "def", "?"],
    audio: "nanga_def",
    category: 'Salutations',
    tags: ['salutations', 'quotidien']
  },
  {
    original: "Mangi ci jàmm",
    french: "Je vais bien",
    english: "I am fine",
    words: ["Mangi", "ci", "jàmm"],
    audio: "mangi_ci_jamm",
    category: 'Salutations',
    tags: ['salutations', 'réponses']
  },
  {
    original: "Ba beneen yoon",
    french: "À la prochaine fois",
    english: "Until next time",
    words: ["Ba", "beneen", "yoon"],
    category: 'Salutations',
    tags: ['au revoir', 'politesse']
  },
  {
    original: "Fanaanal ak jàmm",
    french: "Bonne nuit",
    english: "Good night",
    words: ["Fanaanal","ak","jàmm"],
    category: 'Salutations',
    tags: ['Bonne', 'nuit']
  },
 
  {
    original: "Salamalekum Serigne bi",
    french: "Bonjour Serigne",
    english: "Hello Serigne",
    words: ["Salamalekum","Serigne","bi"],
    audio: "salamalekum_serigne_bi",
    category: 'Salutations',
    tags: ['salutations', 'traditionnel']
  },
  {
    original: "Malekum salaam",
    french: "Bonjour ",
    english: "Hello",
    words: ["Malekum", "salaam"],
    category: 'Salutations',
    tags: ['salutations', 'réponse']
  },
  {
    original: "Jàmm ak jàmm",
    french: "Au revoir",
    english: "Goodbye",
    words: ["Jàmm", "ak", "jàmm"],
    audio: "jamm_ak_jamm",
    category: 'Salutations',
    tags: ['au revoir', 'traditionnel']
  },
  {
    original: "Nungeen def ?",
    french: "Comment allez-vous ?",
    english: "How are you?",
    words: ["Nungeen", "def", "?"],
    audio: "nungeen_def",
    category: 'Salutations',
    tags: ['salutations', 'pluriel']
  },
  {
    original: "Mangi fi rekk",
    french: "Je vais bien ",
    english: "I am fine",
    words: ["Mangi", "fi", "rekk"],
    audio: "mangi_fi_rekk",
    category: 'Salutations',
    tags: ['réponse', 'informel']
  },
  {
    original: "Bu suba ak jamm",
    french: "À demain",
    english: "See you tomorrow",
    words: ["Bu", "suba", "ak", "jamm"],
    category: 'Salutations',
    tags: ['au revoir', 'temps']
  },
  {
    original: "Baal ma",
    french: "Pardon",
    english: "Sorry",
    words: ["Baal", "ma"],
    audio: "baal_ma",
    category: 'Salutations',
    tags: ['politesse', 'excuses']
  },

  // Pronom objet ma — demandes polies
  
  {
    original: "Woo ma",
    french: "Appelle-moi",
    english: "Call me",
    words: ["Woo", "ma"],
    audio: "woo_ma",
    category: 'Salutations',
    tags: ['pronom-objet', 'impératif', 'grammaire']
  },


  // Présentation
  {
    original: "Mangi jang wolof",
    french: "J'apprends le wolof",
    english: "I am learning Wolof",
    words: ["Mangi", "jang", "wolof"],
    audio: "mangi_jang_wolof",
    category: 'Présentation',
    tags: ['apprentissage', 'présentation']
  },
  {
    original: "Noo tudd ?",
    french: "Comment t'appelles-tu ?",
    english: "What is your name?",
    words: ["Noo", "tudd", "?"],
    category: 'Présentation',
    tags: ['présentation', 'nom']
  },
  {
    original: "Fo dëkk ?",
    french: "Où habites-tu ?",
    english: "Where do you live?",
    words: ["Fo", "dëkk", "?"],
    category: 'Présentation',
    tags: ['lieu', 'habitation']
  },
  {
    original: "Mangi dëkk Dakar",
    french: "J'habite à Dakar",
    english: "I live in Dakar",
    words: ["Mangi", "dëkk", "Dakar"],
    category: 'Présentation',
    tags: ['lieu', 'ville']
  },
  {
    original: "Fan nga joge ?",
    french: "D'où viens-tu ?",
    english: "Where are you from?",
    words: ["Fan", "nga", "joge", "?"],
    audio: "fan_nga_joge",
    category: 'Présentation',
    tags: ['origine', 'question']
  },

   {
    original: "Foo joge ?",
    french: "D'où viens-tu ?",
    english: "Where are you from?",
    words: ["Foo", "joge", "?"],
    audio: "Foo_joge",
    category: 'Présentation',
    tags: ['origine', 'question']
  },
  {
    original: "Sama tur mooy abdoulaye",
    french: "Mon nom est Abdoulaye",
    english: "My name is Abdoulaye",
    words: ["Sama", "tur", "mooy","Abdoulaye"],
    category: 'Présentation',
    tags: ['nom', 'présentation']
  },
  {
    original: "Naata att nga am ?",
    french: "Quel âge as-tu ?",
    english: "How old are you?",
    words: ["Naata", "att", "nga", "am", "?"],
    audio: "Naata_att_nga_am",
    category: 'Présentation',
    tags: ['âge', 'question']
  },
  {
    original: "Ana sa yaay ?",
    french: "Où est ta mère ?",
    english: "Where is your mother?",
    words: ["Ana", "sa", "yaay", "?"],
    category: 'Présentation',
    tags: ['famille', 'question', 'possessif']
  },
  {
    original: "Sama baay mangi fi",
    french: "Mon père est ici",
    english: "My father is here",
    words: ["Sama", "baay", "mangi", "fi"],
    category: 'Présentation',
    tags: ['famille', 'présent', 'ngi']
  },
  {
    original: "Dama am bënn xarit",
    french: "J'ai un ami",
    english: "I have a friend",
    words: ["Dama", "am", "bënn", "xarit"],
    category: 'Présentation',
    tags: ['famille', 'nombres', 'focus']
  },
  {
    original: "Sama doom mangi jàng",
    french: "Mon enfant est en train d'étudier",
    english: "My child is studying",
    words: ["Sama", "doom", "mangi", "jàng"],
    category: 'Présentation',
    tags: ['famille', 'ngi', 'possessif']
  },
  {
    original: "Dama am ñaari doom",
    french: "J'ai deux enfants",
    english: "I have two children",
    words: ["Dama", "am", "ñaari", "doom"],
    category: 'Présentation',
    tags: ['famille', 'nombres', 'focus']
  },
  {
    original: "Sama xarit mangi fi",
    french: "Mon ami est ici",
    english: "My friend is here",
    words: ["Sama", "xarit", "mangi", "fi"],
    category: 'Présentation',
    tags: ['famille', 'ngi', 'possessif']
  },
  {
    original: "Sa xarit rafet na",
    french: "Ton ami est beau",
    english: "Your friend is handsome",
    words: ["Sa", "xarit", "rafet", "na"],
    category: 'Présentation',
    tags: ['famille', 'description', 'possessif']
  },
  {
    original: "Sama maam mungi toog",
    french: "Mon grand-père est assis",
    english: "My grandfather is sitting",
    words: ["Sama", "maam", "mungi", "toog"],
    category: 'Présentation',
    tags: ['famille', 'ngi', 'possessif']
  },
  {
    original: "Sa baay am na ñaari xar",
    french: "Ton père a deux moutons",
    english: "Your father has two sheep",
    words: ["Sa", "baay", "am", "na", "ñaar", "xar"],
    category: 'Présentation',
    tags: ['famille', 'animaux', 'nombres', 'possessif']
  },


  {
    original: "Maa la woo",
    french: "C'est moi qui t'ai appelé",
    english: "It is I who called you",
    words: ["Maa", "la", "woo"],
    category: 'Présentation',
    tags: ['emphatique', 'pronom-objet', 'grammaire']
  },

  {
    original: "Moom moo am sama doom yi",
    french: "C'est lui/elle qui a mes enfants",
    english: "It is he/she who has my children",
    words: ["Moom", "moo", "am", "sama", "doom", "yi"],
    category: 'Présentation',
    tags: ['emphatique', 'famille', 'possessif', 'grammaire']
  },

  // Pronoms objets — ma / la / ko
  {
    original: "Dama la bëgg",
    french: "Je t'aime",
    english: "I love you",
    words: ["Dama", "la", "bëgg"],
    category: 'Présentation',
    tags: ['pronom-objet', 'sentiment', 'focus', 'grammaire']
  },
  {
    original: "Sama yaay la gis",
    french: "Ma mère t'a vu",
    english: "My mother saw you",
    words: ["Sama", "yaay", "la", "gis"],
    category: 'Présentation',
    tags: ['pronom-objet', 'famille', 'possessif', 'grammaire']
  },
  {
    original: "Jox ko sa tur",
    french: "Donne-lui ton nom",
    english: "Give him/her your name",
    words: ["Jox", "ko", "sa", "tur"],
    category: 'Présentation',
    tags: ['pronom-objet', 'possessif', 'impératif', 'grammaire']
  },

  // Pronoms possessifs — Sunu / Seen / Séen
  {
    original: "Sunu xarit mu ngi fi",
    french: "Notre ami est ici",
    english: "Our friend is here",
    words: ["Sunu", "xarit", "mu", "ngi", "fi"],
    category: 'Présentation',
    tags: ['possessif', 'famille', 'ngi', 'grammaire']
  },
  {
    original: "Seen doom yi dem nañu",
    french: "Vos enfants sont partis",
    english: "Your children have left",
    words: ["Seen", "doom", "yi", "dem", "nañu"],
    category: 'Présentation',
    tags: ['possessif', 'famille', 'pluriel', 'grammaire']
  },

  // Pronoms toniques — Man / Yow / Môom
  {
    original: "Man dama am sama yaay rekk",
    french: "Moi, j'ai seulement ma mère",
    english: "As for me, I only have my mother",
    words: ["Man", "dama", "am", "sama", "yaay", "rekk"],
    category: 'Présentation',
    tags: ['tonique', 'famille', 'focus', 'grammaire']
  },
 

  // Focus verbal Da- — Danga / Dafa
  {
    original: "Danga am fas bi ?",
    french: "Tu as le cheval ?",
    english: "Do you have the horse?",
    words: ["Danga", "am", "fas", "bi", "?"],
    category: 'Présentation',
    tags: ['focus-da', 'animaux', 'question', 'grammaire']
  },
  {
    original: "Dafa am ñett doom",
    french: "Il/Elle a trois enfants",
    english: "He/She has three children",
    words: ["Dafa", "am", "ñett", "doom"],
    category: 'Présentation',
    tags: ['focus-da', 'famille', 'nombres', 'grammaire']
  },

  // Copule di — "être"
  {
    original: "Sama baay di góor",
    french: "Mon père est un homme",
    english: "My father is a man",
    words: ["Sama", "baay", "di", "góor"],
    category: 'Présentation',
    tags: ['copule-di', 'famille', 'identité', 'grammaire']
  },
  {
    original: "Sama doom bu jigéen di rafet",
    french: "Ma fille est belle",
    english: "My daughter is beautiful",
    words: ["Sama", "doom", "bu", "jigéen", "di", "rafet"],
    category: 'Présentation',
    tags: ['copule-di', 'famille', 'description', 'grammaire']
  },

  // Classe nominale M (mi) — liquides, nourriture
  {
    original: "Muus mi rafet na",
    french: "Ce chat est beau",
    english: "This cat is beautiful",
    words: ["Muus", "mi", "rafet", "na"],
    category: 'Présentation',
    tags: ['classe-M', 'animaux', 'démonstratif', 'grammaire']
  },

  // Vie quotidienne
  {
    original: "Dama xiif",
    french: "J'ai faim",
    english: "I am hungry",
    words: ["Dama", "xiif"],
    audio: "dama_xiif",
    category: 'Vie quotidienne',
    tags: ['état', 'nourriture']
  },
  {
    original: "Dama mar",
    french: "J'ai soif",
    english: "I am thirsty",
    words: ["Dama", "mar"],
    audio: "dama_mar",
    category: 'Vie quotidienne',
    tags: ['état', 'boisson']
  },
  {
    original: "Dama sonn",
    french: "Je suis fatigué(e)",
    english: "I am tired",
    words: ["Dama", "sonn"],
    audio: "dama_sonn",
    category: 'Vie quotidienne',
    tags: ['état', 'fatigue']
  },
  {
    original: "Danu doon naan attaaya",
    french: "Nous avons bu du thé",
    english: "We drank tea",
    words: ["Nañu", "naan", "attaaya"],
    category: 'Vie quotidienne',
    tags: ['boisson', 'social']
  },
  {
    original: "Lekk naa ba suur",
    french: "J'ai bien mangé",
    english: "I ate well",
    words: ["Lekk", "naa", "ba", "suur"],
    audio: "lekk_naa_ba_suur",
    category: 'Vie quotidienne',
    tags: ['nourriture', 'satisfaction']
  },
  {
    original: "Damay noppaliku",
    french: "Je me repose",
    english: "I am resting",
    words: ["Damay", "noppaliku"],
    audio: "Damay_noppaliku",
    category: 'Vie quotidienne',
    tags: ['repos', 'activité']
  },
  {
    original: "Lu bees am ?",
    french: "Quoi de neuf ?",
    english: "What's new?",
    words: ["Lu", "bees", "am", "?"],
    category: 'Vie quotidienne',
    tags: ['conversation', 'quotidien']
  },
  {
    original: "Dama feebar",
    french: "Je suis malade",
    english: "I am sick",
    words: ["Dama", "feebar"],
    category: 'Vie quotidienne',
    tags: ['santé', 'état']
  },
  {
    original: "Dama toog ci kër gi",
    french: "Je reste à la maison",
    english: "I am staying home",
    words: ["Dama", "toog", "ci", "kër", "gi"],
    category: 'Vie quotidienne',
    tags: ['maison', 'activité']
  },
  {
    original: "Dama bëgg nelaw",
    french: "J'ai envie de dormir",
    english: "I feel like sleeping",
    words: ["Dama", "bëgg", "nelaw"],
    category: 'Vie quotidienne',
    tags: ['sommeil', 'état']
  },
  {
    original: "Ban waxtu jot ?",
    french: "Quelle heure est-il ?",
    english: "What time is it?",
    words: ["Ban", "waxtu", "jot", "?"],
    category: 'Vie quotidienne',
    tags: ['temps', 'question']
  },
  {
    original: "Ndax la sa bopp feebar ?",
    french: "Est-ce que ta tête te fait mal ?",
    english: "Does your head hurt?",
    words: ["Ndax", "la", "sa", "bopp", "feebar", "?"],
    category: 'Vie quotidienne',
    tags: ['question-ndax', 'corps', 'santé', 'grammaire']
  },
  {
    original: "Mangi fii di lekk",
    french: "Je suis en train de manger",
    english: "I am eating",
    words: ["Mangi", "fii", "di", "lekk"],
    category: 'Vie quotidienne',
    tags: ['progressif', 'présent', 'action']
  },
  {
    original: "Yéénangi toog",
    french: "Vous êtes assis",
    english: "You are sitting",
    words: ["Yéénangi", "toog"],
    category: 'Vie quotidienne',
    tags: ['présent', 'état', 'position']
  },
  {
    original: "Ñungë nellaw",
    french: "Ils dorment",
    english: "They are sleeping",
    words: ["Ñungë", "nellaw"],
    category: 'Vie quotidienne',
    tags: ['présent', 'action', 'sommeil']
  },
  {
    original: "mungiy dem",
    french: "Il est en train de partir",
    english: "He is leaving",
    words: ["mu","ngi", "dem"],
    category: 'Vie quotidienne',
    tags: ['mouvement', 'départ', 'progressif']
  },
  {
    original: "yangiy tog",
    french: "Tu es en train de cuisiner",
    english: "You are cooking",
    words: ["yangiy", "tog"],
    category: 'Vie quotidienne',
    tags: ['progressif', 'cuisine']
  },
  {
    original: "yéénangiy dox",
    french: "Vous êtes en train de marcher",
    english: "You are walking",
    words: ["yéénangiy", "dox"],
    category: 'Vie quotidienne',
    tags: ['progressif', 'mouvement']
  },
  {
    original: "Job dafay liggey",
    french: "Diop est en train de travailler",
    english: "Diop is working",
    words: ["Job", "dafay", "liggey"],
    category: 'Vie quotidienne',
    tags: ['travail', 'progressif', 'nom propre']
  },
  {
    original: "Kañ ngëy xiim attaya ?",
    french: "Quand fais-tu du thé ?",
    english: "When do you make tea?",
    words: ["Kañ", "ngëy", "xiim", "attaya", "?"],
    category: 'Vie quotidienne',
    tags: ['thé', 'temps', 'question']
  },
  {
    original: "Cééb bi mo gënë neex cééb boobu",
    french: "Ce riz-ci est meilleur que celui-là",
    english: "This rice is better than that one",
    words: ["Cééb", "bi", "mo", "gënë", "neex", "cééb", "boobu"],
    category: 'Vie quotidienne',
    tags: ['comparaison', 'nourriture', 'qualité']
  },
  {
    original: "Man maa gënë yakkamti yow",
    french: "Je suis plus pressé que toi",
    english: "I am in more of a hurry than you",
    words: ["Man", "maa", "gënë", "yakkamti", "yow"],
    category: 'Vie quotidienne',
    tags: ['comparaison', 'état', 'urgence']
  },

  // Présent habituel (-ay) — action régulière
  {
    original: "Dafay lekk cééb suba",
    french: "Il/Elle mange habituellement du riz le matin",
    english: "He/She usually eats rice in the morning",
    words: ["Dafay", "lekk", "cééb", "suba"],
    category: 'Vie quotidienne',
    tags: ['présent-habituel', 'nourriture', 'temps', 'grammaire']
  },
  {
    original: "Dangay naan kafee ngoon ?",
    french: "Tu bois habituellement du café l'après-midi ?",
    english: "Do you usually drink coffee in the afternoon?",
    words: ["Dangay", "naan", "kafee", "ngoon", "?"],
    category: 'Vie quotidienne',
    tags: ['présent-habituel', 'nourriture', 'temps', 'grammaire']
  },

  // Passé résultatif (naa / dem na) — action accomplie
  {
    original: "Naan naa ndox mi",
    french: "J'ai bu de l'eau",
    english: "I drank water",
    words: ["Naan", "naa", "ndox", "mi"],
    category: 'Vie quotidienne',
    tags: ['passé-résultatif', 'nourriture', 'grammaire']
  },
  {
    original: "Feebar nga démb ?",
    french: "Tu étais malade hier ?",
    english: "Were you sick yesterday?",
    words: ["Feebar", "nga", "démb", "?"],
    category: 'Vie quotidienne',
    tags: ['passé-résultatif', 'corps', 'temps', 'grammaire']
  },

  // Passé emphatique (Dama/Dafa) — focus sur l'action accomplie
  {
    original: "Dama lekk céébu jën",
    french: "J'ai mangé du riz au poisson",
    english: "I ate rice with fish",
    words: ["Dama", "lekk", "céébu", "jën"],
    category: 'Vie quotidienne',
    tags: ['passé-emphatique', 'nourriture', 'grammaire']
  },
  {
    original: "Dafa lekk màafe bi",
    french: "Il/Elle a mangé la sauce à l'arachide",
    english: "He/She ate the peanut sauce",
    words: ["Dafa", "lekk", "màafe", "bi"],
    category: 'Vie quotidienne',
    tags: ['passé-emphatique', 'nourriture', 'grammaire']
  },

  // Imparfait progressif (doon) — action en cours dans le passé
  {
    original: "Dama doon lekk bi nga ñëw",
    french: "J'étais en train de manger quand tu es arrivé",
    english: "I was eating when you arrived",
    words: ["Dama", "doon", "lekk", "bi", "nga", "ñëw"],
    category: 'Vie quotidienne',
    tags: ['imparfait-progressif', 'nourriture', 'grammaire']
  },
  {
    original: "Dafa doon naan kafee bi",
    french: "Il/Elle était en train de boire le café",
    english: "He/She was drinking the coffee",
    words: ["Dafa", "doon", "naan", "kafee", "bi"],
    category: 'Vie quotidienne',
    tags: ['imparfait-progressif', 'nourriture', 'grammaire']
  },

  // Futur (Dinaa / Dinga / Dina) — action à venir
  {
    original: "Dinaa lekk cééb bi ngoon",
    french: "Je mangerai du riz l'après-midi",
    english: "I will eat rice in the afternoon",
    words: ["Dinaa", "lekk", "cééb", "bi", "ngoon"],
    category: 'Vie quotidienne',
    tags: ['futur', 'nourriture', 'temps', 'grammaire']
  },
  {
    original: "Dinga dem fa altine ?",
    french: "Tu iras là-bas lundi ?",
    english: "Will you go there on Monday?",
    words: ["Dinga", "dem", "fa", "altine", "?"],
    category: 'Vie quotidienne',
    tags: ['futur', 'temps', 'question', 'grammaire']
  },

  // Impératif affirmatif (-al) — ordre ou invitation
  {
    original: "Lekkaal mburu bi",
    french: "Mange le pain !",
    english: "Eat the bread!",
    words: ["Lekkaal", "mburu", "bi"],
    category: 'Vie quotidienne',
    tags: ['impératif', 'nourriture', 'grammaire']
  },
  {
    original: "Naanal ndox mi",
    french: "Bois l'eau !",
    english: "Drink the water!",
    words: ["Naanal", "ndox", "mi"],
    category: 'Vie quotidienne',
    tags: ['impératif', 'nourriture', 'grammaire']
  },

  // Impératif négatif (Bul) — interdiction
  {
    original: "Bul lekk màafe bi",
    french: "Ne mange pas la sauce à l'arachide !",
    english: "Don't eat the peanut sauce!",
    words: ["Bul", "lekk", "màafe", "bi"],
    category: 'Vie quotidienne',
    tags: ['impératif-négatif', 'nourriture', 'grammaire']
  },
  {
    original: "Bul naan kafee bi guddi",
    french: "Ne bois pas le café la nuit !",
    english: "Don't drink coffee at night!",
    words: ["Bul", "naan", "kafee", "bi", "guddi"],
    category: 'Vie quotidienne',
    tags: ['impératif-négatif', 'nourriture', 'temps', 'grammaire']
  },

  // Négation (-ul / -uma) — action non accomplie ou état négatif
  {
    original: "Lekkuma cééb bi",
    french: "Je n'ai pas mangé le riz",
    english: "I did not eat the rice",
    words: ["Lekkuma", "cééb", "bi"],
    category: 'Vie quotidienne',
    tags: ['négation', 'nourriture', 'grammaire']
  },
  {
    original: "Sama bopp feebarul",
    french: "Ma tête ne fait pas mal",
    english: "My head does not hurt",
    words: ["Sama", "bopp", "feebarul"],
    category: 'Vie quotidienne',
    tags: ['négation', 'corps', 'grammaire']
  },

  // Négation future (Duma / Du) — refus ou impossibilité
  {
    original: "Duma lekk màafe bi",
    french: "Je ne mangerai pas la sauce à l'arachide",
    english: "I will not eat the peanut sauce",
    words: ["Duma", "lekk", "màafe", "bi"],
    category: 'Vie quotidienne',
    tags: ['négation-future', 'nourriture', 'grammaire']
  },
  {
    original: "Du dem màrse ba altine",
    french: "Il/Elle n'ira pas au marché lundi",
    english: "He/She will not go to the market on Monday",
    words: ["Du", "dem", "màrse", "ba", "altine"],
    category: 'Vie quotidienne',
    tags: ['négation-future', 'temps', 'grammaire']
  },

  // Questions — Lan / Kan / Naka
  {
    original: "Lan ngëy lekk ?",
    french: "Qu'est-ce que tu manges ?",
    english: "What are you eating?",
    words: ["Lan", "ngëy", "lekk", "?"],
    category: 'Vie quotidienne',
    tags: ['question-lan', 'nourriture', 'grammaire']
  },
  {
    original: "Naka la sa biir ?",
    french: "Comment va ton ventre ?",
    english: "How is your stomach?",
    words: ["Naka", "la", "sa", "biir", "?"],
    category: 'Vie quotidienne',
    tags: ['question-naka', 'corps', 'grammaire']
  },
  {
    original: "Kan moo tog màafe bi ?",
    french: "Qui a cuisiné la sauce à l'arachide ?",
    english: "Who cooked the peanut sauce?",
    words: ["Kan", "moo", "tog", "màafe", "bi", "?"],
    category: 'Vie quotidienne',
    tags: ['question-kan', 'nourriture', 'grammaire']
  },

  // Couleurs avec nourriture
  {
    original: "Cééb bi weex na",
    french: "Le riz est blanc",
    english: "The rice is white",
    words: ["Cééb", "bi", "weex", "na"],
    category: 'Vie quotidienne',
    tags: ['couleur', 'nourriture', 'grammaire']
  },
  {
    original: "Mango bi xonq na",
    french: "La mangue est rouge",
    english: "The mango is red",
    words: ["Mango", "bi", "xonq", "na"],
    category: 'Vie quotidienne',
    tags: ['couleur', 'nourriture', 'grammaire']
  },
  {
    original: "Jën wi ñuul na",
    french: "Le poisson est foncé",
    english: "The fish is dark",
    words: ["Jën", "wi", "ñuul", "na"],
    category: 'Vie quotidienne',
    tags: ['couleur', 'nourriture', 'grammaire']
  },

  // Corps avec grammaire
  {
    original: "Sama loxo bi feebar na",
    french: "Ma main me fait mal",
    english: "My hand hurts",
    words: ["Sama", "loxo", "bi", "feebar", "na"],
    category: 'Vie quotidienne',
    tags: ['corps', 'état', 'possessif', 'grammaire']
  },
  {
    original: "Sama bët yi ñuul nañu",
    french: "Mes yeux sont sombres",
    english: "My eyes are dark",
    words: ["Sama", "bët", "yi", "ñuul", "nañu"],
    category: 'Vie quotidienne',
    tags: ['corps', 'couleur', 'pluriel', 'grammaire']
  },

  // Au marché
  {
    original: "Nañu dem ca màrse ba",
    french: "Allons au marché",
    english: "Let's go to the market",
    words: ["Nañu", "dem", "ca", "màrse", "ba"],
    category: 'Au marché',
    tags: ['déplacement', 'shopping']
  },
  {
    original: "Ñaata la ?",
    french: "Combien ça coûte ?",
    english: "How much does it cost?",
    words: ["Ñaata", "la", "?"],
    category: 'Au marché',
    tags: ['prix', 'achat']
  },
  {
    original: "Dafa jafe",
    french: "C'est trop cher",
    english: "It's too expensive",
    words: ["Dafa", "jafe"],
    category: 'Au marché',
    tags: ['prix', 'négociation']
  },
  {
    original: "Waññi ko tuuti",
    french: "Baisse un peu le prix",
    english: "Lower the price a little",
    words: ["Waññi", "ko", "tuuti"],
    category: 'Au marché',
    tags: ['négociation', 'prix']
  },
  {
    original: "Am nga doomi jën ?",
    french: "As-tu du poisson ?",
    english: "Do you have fish?",
    words: ["Am", "nga", "doomi", "jën", "?"],
    category: 'Au marché',
    tags: ['nourriture', 'question']
  },
  {
    original: "Dama bëgg jënd",
    french: "Je veux acheter",
    english: "I want to buy",
    words: ["Dama", "bëgg", "jënd"],
    category: 'Au marché',
    tags: ['achat', 'intention']
  },
  {
    original: "Ndax am nga céébu jën ?",
    french: "As-tu du thiéboudienne ?",
    english: "Do you have thieboudienne?",
    words: ["Ndax", "am", "nga", "céébu", "jën", "?"],
    category: 'Au marché',
    tags: ['question-ndax', 'nourriture', 'grammaire']
  },
  {
    original: "Fan la butik bi nekk ?",
    french: "Où se trouve la boutique ?",
    english: "Where is the shop?",
    words: ["Fan", "la", "butik", "bi", "nekk", "?"],
    category: 'Au marché',
    tags: ['lieu', 'question']
  },
  {
    original: "Dama bëgg wecciku",
    french: "Je veux échanger de l'argent",
    english: "I want to exchange money",
    words: ["Dama", "bëgg", "wecciku"],
    category: 'Au marché',
    tags: ['argent', 'service']
  },
  {
    original: "Lan nga bëgg jënd ?",
    french: "Qu'est-ce que tu veux acheter ?",
    english: "What do you want to buy?",
    words: ["Lan", "nga", "bëgg", "jënd", "?"],
    category: 'Au marché',
    tags: ['question-lan', 'achat', 'grammaire']
  },
  {
    original: "Dama bëgg jend jën wu yomb",
    french: "Je veux acheter du poisson bon marché",
    english: "I want to buy cheap fish",
    words: ["Dama", "bëgg", "jend", "jën", "wu", "yomb"],
    category: 'Au marché',
    tags: ['achat', 'nourriture', 'prix']
  },
  {
    original: "yangi tog cééb-u yapp",
    french: "Tu cuisines du riz à la viande",
    english: "You are cooking rice with meat",
    words: ["yangi", "tog", "cééb-u", "yapp"],
    category: 'Au marché',
    tags: ['cuisine', 'riz', 'viande']
  },
  {
    original: "jend jën wu jafé",
    french: "Acheter du poisson cher",
    english: "Buy expensive fish",
    words: ["jend", "jën", "wu", "jafé"],
    category: 'Au marché',
    tags: ['achat', 'poisson', 'prix']
  },
  {
    original: "Jëndal ma cééb bi",
    french: "Achète-moi le riz !",
    english: "Buy me the rice!",
    words: ["Jëndal", "ma", "cééb", "bi"],
    category: 'Au marché',
    tags: ['impératif', 'nourriture', 'pronom-objet', 'grammaire']
  },
  {
    original: "Ñaata jën ngëy jel tey ?",
    french: "Combien de poissons vas-tu prendre aujourd'hui ?",
    english: "How many fish are you going to take today?",
    words: ["Ñaata", "jën", "ngëy", "jel", "tey", "?"],
    category: 'Au marché',
    tags: ['quantité', 'poisson', 'question', 'temps']
  },
  {
    original: "Ñaata kilo ceeb ngëy mën a jel ?",
    french: "Combien de kilos de riz peux-tu prendre ?",
    english: "How many kilos of rice can you take?",
    words: ["Ñaata", "kilo", "ceeb", "ngëy", "mën", "a", "jel", "?"],
    category: 'Au marché',
    tags: ['poids', 'riz', 'capacité', 'question']
  },

  // Passé résultatif au marché
  {
    original: "Jënd nga ñaata jën démb ?",
    french: "Tu as acheté combien de poissons hier ?",
    english: "How many fish did you buy yesterday?",
    words: ["Jënd", "nga", "ñaata", "jën", "démb", "?"],
    category: 'Au marché',
    tags: ['passé-résultatif', 'question-ñaata', 'nourriture', 'temps', 'grammaire']
  },
  {
    original: "Naan naa bissap bi",
    french: "J'ai bu le bissap",
    english: "I drank the bissap",
    words: ["Naan", "naa", "bissap", "bi"],
    category: 'Au marché',
    tags: ['passé-résultatif', 'nourriture', 'grammaire']
  },

  // Futur au marché
  {
    original: "Dinaa jënd cééb ak jën",
    french: "J'achèterai du riz et du poisson",
    english: "I will buy rice and fish",
    words: ["Dinaa", "jënd", "cééb", "ak", "jën"],
    category: 'Au marché',
    tags: ['futur', 'nourriture', 'grammaire']
  },
  {
    original: "Dinga dem màrse ba altine ?",
    french: "Tu iras au marché lundi ?",
    english: "Will you go to the market on Monday?",
    words: ["Dinga", "dem", "màrse", "ba", "altine", "?"],
    category: 'Au marché',
    tags: ['futur', 'temps', 'question', 'grammaire']
  },

  // Impératif au marché
  {
    original: "Jëndal ma mburu bi",
    french: "Achète-moi le pain !",
    english: "Buy me bread!",
    words: ["Jëndal", "ma", "mburu", "bi"],
    category: 'Au marché',
    tags: ['impératif', 'nourriture', 'pronom-objet', 'grammaire']
  },
  {
    original: "Waññi ko tuuti, dafa jafe lool",
    french: "Baisse-le un peu, c'est vraiment trop cher",
    english: "Lower it a bit, it's really too expensive",
    words: ["Waññi", "ko", "tuuti", "dafa", "jafe", "lool"],
    category: 'Au marché',
    tags: ['négociation', 'impératif', 'grammaire']
  },

  // Négation au marché
  {
    original: "Màafe bi neexul",
    french: "La sauce à l'arachide n'est pas bonne",
    english: "The peanut sauce is not good",
    words: ["Màafe", "bi", "neexul"],
    category: 'Au marché',
    tags: ['négation', 'nourriture', 'grammaire']
  },
  {
    original: "Duma jënd yàpp bu jafe",
    french: "Je n'achèterai pas de viande chère",
    english: "I will not buy expensive meat",
    words: ["Duma", "jënd", "yàpp", "bu", "jafe"],
    category: 'Au marché',
    tags: ['négation-future', 'nourriture', 'grammaire']
  },

  // Questions au marché — Lan / Kan
  {
    original: "Lan nga jënd démb ?",
    french: "Qu'est-ce que tu as acheté hier ?",
    english: "What did you buy yesterday?",
    words: ["Lan", "nga", "jënd", "démb", "?"],
    category: 'Au marché',
    tags: ['question-lan', 'temps', 'grammaire']
  },
  {
    original: "Kan moo tog céébu jën bi ?",
    french: "Qui a cuisiné le thiéboudienne ?",
    english: "Who cooked the thieboudienne?",
    words: ["Kan", "moo", "tog", "céébu", "jën", "bi", "?"],
    category: 'Au marché',
    tags: ['question-kan', 'nourriture', 'grammaire']
  },

  // Couleurs au marché
  {
    original: "Jën wi xonq na",
    french: "Le poisson est rouge",
    english: "The fish is red",
    words: ["Jën", "wi", "xonq", "na"],
    category: 'Au marché',
    tags: ['couleur', 'nourriture', 'grammaire']
  },
  {
    original: "Yàpp bi ñuul na",
    french: "La viande est foncée",
    english: "The meat is dark",
    words: ["Yàpp", "bi", "ñuul", "na"],
    category: 'Au marché',
    tags: ['couleur', 'nourriture', 'grammaire']
  },

  // Voyages
  {
    original: "Fan la gaar bi nekk ?",
    french: "Où se trouve la gare ?",
    english: "Where is the station?",
    words: ["Fan", "la", "gaar", "bi", "nekk", "?"],
    category: 'Voyages',
    tags: ['transport', 'lieu']
  },
  {
    original: "Kañ la taksi bi di ñëw ?",
    french: "Quand arrive le taxi ?",
    english: "When does the taxi arrive?",
    words: ["Kañ", "la", "taksi", "bi", "di", "ñëw", "?"],
    category: 'Voyages',
    tags: ['transport', 'horaire']
  },
  {
    original: "Wóor na ma dama réer",
    french: "Je crois que je suis perdu(e)",
    english: "I think I am lost",
    words: ["Wóor", "na", "ma", "dama", "réer"],
    category: 'Voyages',
    tags: ['orientation', 'aide']
  },
  {
    original: "Ban néég nga bëgg ci kër bi ?",
    french: "Quelle chambre veux-tu dans la maison ?",
    english: "Which room do you want in the house?",
    words: ["Ban", "néég", "nga", "bëgg", "ci", "kër", "bi", "?"],
    category: 'Voyages',
    tags: ['hébergement', 'information', 'préposition']
  },
  {
    original: "Kañ ngay dem gaar bi ?",
    french: "Quand pars-tu à la gare ?",
    english: "When are you leaving for the station?",
    words: ["Kañ", "ngay", "dem", "gaar", "bi", "?"],
    category: 'Voyages',
    tags: ['transport', 'gare', 'question']
  },
  {
    original: "Fan ngay dem léegi ?",
    french: "Où vas-tu maintenant ?",
    english: "Where are you going now?",
    words: ["Fan", "ngay", "dem", "léegi", "?"],
    category: 'Voyages',
    tags: ['direction', 'adverbe', 'question']
  },
  {
    original: "Ndax am nga xaalis ngir dem ?",
    french: "As-tu de l'argent pour partir ?",
    english: "Do you have money to leave?",
    words: ["Ndax", "am", "nga", "xaalis", "ngir", "dem", "?"],
    category: 'Voyages',
    tags: ['transport', 'argent', 'préposition']
  },
  {
    original: "Fii mooy gaar bi",
    french: "C'est ici la gare",
    english: "This is the station",
    words: ["Fii", "mooy", "gaar", "bi"],
    category: 'Voyages',
    tags: ['gare', 'lieu', 'localisation']
  },
  {
    original: "Ndax baax na yoon wi ?",
    french: "La route est-elle bonne ?",
    english: "Is the road good?",
    words: ["Ndax", "baax", "na", "yoon", "wi", "?"],
    category: 'Voyages',
    tags: ['route', 'adjectif', 'question']
  },
  {
    original: "Ndax man naa jël sama woto ?",
    french: "Puis-je prendre ma voiture ?",
    english: "Can I take my car?",
    words: ["Ndax", "man", "naa", "jël", "sama", "woto", "?"],
    category: 'Voyages',
    tags: ['voiture', 'permission', 'question']
  },
  {
    original: "Dama bëgg ndimbal ci gaar bi",
    french: "J'ai besoin d'aide à la gare",
    english: "I need help at the station",
    words: ["Dama", "bëgg", "ndimbal", "ci", "gaar", "bi"],
    category: 'Voyages',
    tags: ['aide', 'gare', 'préposition']
  },
  {
    original: "Wax ma yoon wi jëm",
    french: "Indique-moi le chemin vers",
    english: "Show me the way to",
    words: ["Wax", "ma", "yoon", "wi", "jëm"],
    category: 'Voyages',
    tags: ['direction', 'aide']
  },
  {
    original: "Taksi bi dafa dem Ndakaru",
    french: "Le taxi va à Dakar",
    english: "The taxi is going to Dakar",
    words: ["Taksi", "bi", "dafa", "dem", "Ndakaru"],
    category: 'Voyages',
    tags: ['transport', 'destination', 'ville']
  },
  {
    original: "Jël saxaar gi léegi",
    french: "Prends le train maintenant",
    english: "Take the train now",
    words: ["Jël", "saxaar", "gi", "léegi"],
    category: 'Voyages',
    tags: ['train', 'transport', 'adverbe']
  },
  {
    original: "Lu tax ngëy jel saxaar gi ?",
    french: "Pourquoi prends-tu le train ?",
    english: "Why are you taking the train?",
    words: ["Lu", "tax", "ngëy", "jel", "saxaar", "gi", "?"],
    category: 'Voyages',
    tags: ['raison', 'train', 'question', 'motivation']
  },
  {
    original: "yoon wi digganté Ndakaru ak Ndar sori na",
    french: "La route entre Dakar et Saint-Louis est longue",
    english: "The road between Dakar and Saint-Louis is long",
    words: ["yoon", "wi", "digganté", "Ndakaru", "ak", "Ndar", "sorinë"],
    category: 'Voyages',
    tags: ['route', 'distance', 'villes', 'géographie']
  },
  {
    original: "Subë, damay dem dekk-bë",
    french: "Demain, j'irai en ville",
    english: "Tomorrow I will go to town",
    words: ["Subë", "damay", "dem", "dekk-bë"],
    category: 'Voyages',
    tags: ['futur', 'ville', 'déplacement']
  },
  {
    original: "Kañ ngë fii ñëw ?",
    french: "Quand es-tu venu(e) ici ?",
    english: "When did you come here?",
    words: ["Kañ", "ngë", "fii", "ñëw", "?"],
    category: 'Voyages',
    tags: ['temps', 'question', 'passé']
  },
  {
    original: "fan ngë dem ?",
    french: "Où vas-tu ?",
    english: "Where are you going?",
    words: ["fan", "ngë", "dem", "?"],
    category: 'Voyages',
    tags: ['direction', 'question', 'mouvement']
  },

  {
    original: "Siis bi nekk ci biir woto bi",
    french: "La valise est dans la voiture",
    english: "The suitcase is in the car",
    words: ["Siis", "bi", "nekk", "ci", "biir", "woto", "bi"],
    category: 'Voyages',
    tags: ['préposition', 'voiture', 'localisation']
  },
  {
    original: "Léetar bi nekk ci kaw taabal bi",
    french: "La lettre est sur la table",
    english: "The letter is on the table",
    words: ["Léetar", "bi", "nekk", "ci", "kaw", "taabal", "bi"],
    category: 'Voyages',
    tags: ['préposition', 'table', 'localisation']
  },
  {
    original: "Yoon wi dafa gudd lool",
    french: "La route est très longue",
    english: "The road is very long",
    words: ["Yoon", "wi", "dafa", "gudd", "lool"],
    category: 'Voyages',
    tags: ['route', 'adjectif', 'adverbe']
  },
  {
    original: "Dama dem gaar bi te dama jël saxaar gi",
    french: "Je suis allé à la gare et j'ai pris le train",
    english: "I went to the station and took the train",
    words: ["Dama", "dem", "gaar", "bi", "te", "dama", "jël", "saxaar", "gi"],
    category: 'Voyages',
    tags: ['conjonction', 'gare', 'train', 'déplacement']
  },
  {
    original: "Dama bëgg dem waaye dama xiif",
    french: "Je veux partir mais j'ai faim",
    english: "I want to leave but I am hungry",
    words: ["Dama", "bëgg", "dem", "waaye", "dama", "xiif"],
    category: 'Voyages',
    tags: ['conjonction', 'déplacement', 'état']
  },
  {
    original: "Dem ndank ndank, yoon wi dafa jafé",
    french: "Va doucement, la route est difficile",
    english: "Go slowly, the road is difficult",
    words: ["Dem", "ndank", "ndank", "yoon", "wi", "dafa", "jafé"],
    category: 'Voyages',
    tags: ['adverbe', 'route', 'adjectif']
  },
  {
    original: "Su ngay dem gaar bi, wax ma",
    french: "Si tu pars à la gare, dis-moi",
    english: "If you leave for the station, tell me",
    words: ["Su", "ngay", "dem", "gaar", "bi", "wax", "ma"],
    category: 'Voyages',
    tags: ['conjonction', 'condition', 'gare']
  },

  // Famille et relations
  {
    original: "Ndax am nga waa kër ?",
    french: "As-tu une famille ?",
    english: "Do you have a family?",
    words: ["Ndax", "am", "nga", "waa", "kër", "?"],
    category: 'Famille et relations',
    tags: ['famille', 'questions']
  },
  {
    original: "Ñaata doom nga am ?",
    french: "Combien d'enfants as-tu ?",
    english: "How many children do you have?",
    words: ["Ñaata", "doom", "nga", "am", "?"],
    category: 'Famille et relations',
    tags: ['famille', 'enfants']
  },
  {
    original: "Sama jabar la",
    french: "C'est ma femme",
    english: "This is my wife",
    words: ["Sama", "jabar", "la"],
    category: 'Famille et relations',
    tags: ['famille', 'présentation']
  },
  {
    original: "Kañ ngay takk ?",
    french: "Quand te maries-tu ?",
    english: "When are you getting married?",
    words: ["Kañ", "ngay", "takk", "?"],
    category: 'Famille et relations',
    tags: ['mariage', 'question']
  },
  {
    original: "Sama doom ju góor ji",
    french: "Mon fils",
    english: "My son",
    words: ["Sama", "doom", "ju", "góor", "ji"],
    category: 'Famille et relations',
    tags: ['famille', 'enfant']
  },
  {
    original: "Sama doom ju jigéen ji",
    french: "Ma fille",
    english: "My daughter",
    words: ["Sama", "doom", "ju", "jigéen", "ji"],
    category: 'Famille et relations',
    tags: ['famille', 'enfant']
  },
  {
    original: "Sama baay mooy",
    french: "Mon père est",
    english: "My father is",
    words: ["Sama", "baay", "mooy"],
    category: 'Famille et relations',
    tags: ['famille', 'parent']
  },
  {
    original: "Sama yaay mooy",
    french: "Ma mère est",
    english: "My mother is",
    words: ["Sama", "yaay", "mooy"],
    category: 'Famille et relations',
    tags: ['famille', 'parent']
  },
  {
    original: "Mag bi la",
    french: "C'est l'aîné(e)",
    english: "This is the eldest",
    words: ["Mag", "bi", "la"],
    category: 'Famille et relations',
    tags: ['famille', 'description']
  },
  {
    original: "Rakk bi la",
    french: "C'est le/la cadet(te)",
    english: "This is the youngest",
    words: ["Rakk", "bi", "la"],
    category: 'Famille et relations',
    tags: ['famille', 'description']
  },
  {
    original: "Ndax am nga rakk ?",
    french: "As-tu des frères et sœurs cadets ?",
    english: "Do you have younger siblings?",
    words: ["Ndax", "am", "nga", "rakk", "?"],
    category: 'Famille et relations',
    tags: ['famille', 'question']
  },
  {
    original: "Ndax am nga mag ?",
    french: "As-tu des frères et sœurs aînés ?",
    english: "Do you have older siblings?",
    words: ["Ndax", "am", "nga", "mag", "?"],
    category: 'Famille et relations',
    tags: ['famille', 'question']
  },
  {
    original: "Job ak Juf angi toog ci kër gi",
    french: "Diop et Diouf sont assis dans cette maison",
    english: "Diop and Diouf are sitting in this house",
    words: ["Job", "ak", "Juf", "angi", "toog", "ci", "kër", "gi"],
    category: 'Famille et relations',
    tags: ['noms propres', 'localisation', 'état']
  },
  {
    original: "Aysatu angiy dem këram",
    french: "Aïssatou s'en va chez elle",
    english: "Aïssatou is going home",
    words: ["Aysatu", "angiy", "dem", "këram"],
    category: 'Famille et relations',
    tags: ['mouvement', 'possessif', 'maison']
  },
  {
    original: "Tijan angi jang tééré-u Aminata",
    french: "Tidiane lit le livre d'Aminata",
    english: "Tidiane is reading Aminata's book",
    words: ["Tijan", "angi", "jang", "tééré-u", "Aminata"],
    category: 'Famille et relations',
    tags: ['lecture', 'possession', 'livre']
  },

  // Urgences et santé
  {
    original: "Xam nga faj ?",
    french: "Sais-tu soigner ?",
    english: "Do you know how to heal?",
    words: ["Xam", "nga", "faj", "?"],
    category: 'Urgences et santé',
    tags: ['soin', 'question', 'santé']
  },
  {
    original: "Dama tiis lool",
    french: "Je suis très fatigué(e)",
    english: "I am very tired",
    words: ["Dama", "tiis", "lool"],
    category: 'Urgences et santé',
    tags: ['adjectif', 'adverbe', 'état']
  },
  {
    original: "Ñëw léegi, dama bëgg faj",
    french: "Viens maintenant, je veux guérir",
    english: "Come now, I want to get better",
    words: ["Ñëw", "léegi", "dama", "bëgg", "faj"],
    category: 'Urgences et santé',
    tags: ['urgence', 'adverbe', 'soin']
  },
  {
    original: "Dama tàng lool",
    french: "J'ai très chaud",
    english: "I am very hot",
    words: ["Dama", "tàng", "lool"],
    category: 'Urgences et santé',
    tags: ['adjectif', 'adverbe', 'état']
  },
  {
    original: "Joxe ma ndox ngir faj",
    french: "Donne-moi de l'eau pour guérir",
    english: "Give me water to get better",
    words: ["Joxe", "ma", "ndox", "ngir", "faj"],
    category: 'Urgences et santé',
    tags: ['eau', 'soin', 'préposition']
  },
  {
    original: "Ñëw xool ma léegi",
    french: "Viens me voir maintenant",
    english: "Come see me now",
    words: ["Ñëw", "xool", "ma", "léegi"],
    category: 'Urgences et santé',
    tags: ['urgence', 'adverbe', 'soin']
  },
  {
    original: "Dama bëgg faj léegi",
    french: "Je veux guérir maintenant",
    english: "I want to get better now",
    words: ["Dama", "bëgg", "faj", "léegi"],
    category: 'Urgences et santé',
    tags: ['soin', 'adverbe', 'désir']
  },
  {
    original: "Dama bëgg naan ndox",
    french: "Je veux boire de l'eau",
    english: "I want to drink water",
    words: ["Dama", "bëgg", "naan", "ndox"],
    category: 'Urgences et santé',
    tags: ['eau', 'soin', 'besoin']
  },
  {
    original: "Dem woto bi léegi",
    french: "Va à la voiture maintenant",
    english: "Go to the car now",
    words: ["Dem", "woto", "bi", "léegi"],
    category: 'Urgences et santé',
    tags: ['urgence', 'voiture', 'adverbe']
  },
  {
    original: "Dama tiis, dama bëgg nelaw",
    french: "Je suis fatigué(e), je veux dormir",
    english: "I am tired, I want to sleep",
    words: ["Dama", "tiis", "dama", "bëgg", "nelaw"],
    category: 'Urgences et santé',
    tags: ['état', 'adjectif', 'repos']
  },
  {
    original: "Métitu bi dafa am ci sama biir",
    french: "La douleur est dans mon ventre",
    english: "The pain is in my stomach",
    words: ["Métitu", "bi", "dafa", "am", "ci", "sama", "biir"],
    category: 'Urgences et santé',
    tags: ['douleur', 'préposition', 'localisation']
  },
  {
    original: "Ndox bi dafa sedd",
    french: "L'eau est froide",
    english: "The water is cold",
    words: ["Ndox", "bi", "dafa", "sedd"],
    category: 'Urgences et santé',
    tags: ['eau', 'adjectif', 'état']
  },
  {
    original: "Dama tiis ndax dama liggéey lool",
    french: "Je suis fatigué(e) parce que j'ai beaucoup travaillé",
    english: "I am tired because I worked a lot",
    words: ["Dama", "tiis", "ndax", "dama", "liggéey", "lool"],
    category: 'Urgences et santé',
    tags: ['conjonction', 'adjectif', 'adverbe', 'travail']
  },
  {
    original: "Nelaw rekk, bëgg na faj",
    french: "Dors seulement, tu veux guérir",
    english: "Just sleep, you want to get better",
    words: ["Nelaw", "rekk", "bëgg", "na", "faj"],
    category: 'Urgences et santé',
    tags: ['adverbe', 'repos', 'soin']
  },
  {
    original: "Dama naan ndox ngir faj",
    french: "Je bois de l'eau pour guérir",
    english: "I drink water to get better",
    words: ["Dama", "naan", "ndox", "ngir", "faj"],
    category: 'Urgences et santé',
    tags: ['eau', 'préposition', 'soin']
  },
  {
    original: "Bi nga ñëw, xool ma",
    french: "Quand tu viendras, regarde-moi",
    english: "When you come, look at me",
    words: ["Bi", "nga", "ñëw", "xool", "ma"],
    category: 'Urgences et santé',
    tags: ['conjonction', 'temps', 'soin']
  },

  // Loisirs et culture
  {
    original: "Lu ngay bëgg def tey ?",
    french: "Que veux-tu faire aujourd'hui ?",
    english: "What do you want to do today?",
    words: ["Lu", "ngay", "bëgg", "def", "tey", "?"],
    category: 'Loisirs et culture',
    tags: ['activité', 'question']
  },
  {
    original: "Dama bëgg jàng téere bi",
    french: "Je veux lire ce livre",
    english: "I want to read this book",
    words: ["Dama", "bëgg", "jàng", "téere", "bi"],
    category: 'Loisirs et culture',
    tags: ['livre', 'lecture', 'activité']
  },
  {
    original: "Dama bëgg dem lekool bi",
    french: "Je veux aller à l'école",
    english: "I want to go to school",
    words: ["Dama", "bëgg", "dem", "lekool", "bi"],
    category: 'Loisirs et culture',
    tags: ['école', 'déplacement', 'activité']
  },
  {
    original: "Dama bëgg dégg mbalax",
    french: "J'aime écouter le mbalax",
    english: "I like listening to mbalax",
    words: ["Dama", "bëgg", "dégg", "mbalax"],
    category: 'Loisirs et culture',
    tags: ['musique', 'préférence']
  },
  {
    original: "Lu ngay bëgg def ci kër gi ?",
    french: "Que veux-tu faire à la maison ?",
    english: "What do you want to do at home?",
    words: ["Lu", "ngay", "bëgg", "def", "ci", "kër", "gi", "?"],
    category: 'Loisirs et culture',
    tags: ['activité', 'préposition', 'maison', 'question']
  },
  {
    original: "Kañ ngay dem lekool bi ?",
    french: "Quand vas-tu à l'école ?",
    english: "When are you going to school?",
    words: ["Kañ", "ngay", "dem", "lekool", "bi", "?"],
    category: 'Loisirs et culture',
    tags: ['école', 'question', 'temps']
  },
  {
    original: "Dama bëgg bind taalif",
    french: "J'aime écrire des poèmes",
    english: "I like writing poems",
    words: ["Dama", "bëgg", "bind", "taalif"],
    category: 'Loisirs et culture',
    tags: ['écriture', 'art']
  },
  {
    original: "Ndax am nga téere bu baax ?",
    french: "As-tu un bon livre ?",
    english: "Do you have a good book?",
    words: ["Ndax", "am", "nga", "téere", "bu", "baax", "?"],
    category: 'Loisirs et culture',
    tags: ['livre', 'adjectif', 'question']
  },
  {
    original: "Dama bëgg liggéey ci tol bi",
    french: "Je veux travailler dans le champ",
    english: "I want to work in the field",
    words: ["Dama", "bëgg", "liggéey", "ci", "tol", "bi"],
    category: 'Loisirs et culture',
    tags: ['travail', 'préposition', 'champ']
  },
  {
    original: "Ñëw ak ma dem ja bi",
    french: "Viens avec moi au marché",
    english: "Come with me to the market",
    words: ["Ñëw", "ak", "ma", "dem", "ja", "bi"],
    category: 'Loisirs et culture',
    tags: ['invitation', 'conjonction', 'marché']
  },
  {
    original: "Ndax bëgg nga dox ci yoon wi ?",
    french: "Aimes-tu marcher sur la route ?",
    english: "Do you like walking on the road?",
    words: ["Ndax", "bëgg", "nga", "dox", "ci", "yoon", "wi", "?"],
    category: 'Loisirs et culture',
    tags: ['marche', 'préposition', 'route', 'question']
  },
  {
    original: "Fan la lekool bi nekk ?",
    french: "Où est l'école ?",
    english: "Where is the school?",
    words: ["Fan", "la", "lekool", "bi", "nekk", "?"],
    category: 'Loisirs et culture',
    tags: ['école', 'localisation', 'question']
  },
  {
    original: "Xool téere bi, dafa rafet lool",
    french: "Regarde ce livre, il est très beau",
    english: "Look at this book, it's very beautiful",
    words: ["Xool", "téere", "bi", "dafa", "rafet", "lool"],
    category: 'Loisirs et culture',
    tags: ['livre', 'adjectif', 'adverbe']
  },
  {
    original: "Dama liggéey ci kër bi démb",
    french: "J'ai travaillé dans la maison hier",
    english: "I worked in the house yesterday",
    words: ["Dama", "liggéey", "ci", "kër", "bi", "démb"],
    category: 'Loisirs et culture',
    tags: ['travail', 'préposition', 'adverbe', 'passé']
  },
  {
    original: "ñu toog waxtaan tuuti",
    french: "Qu'on s'assoie et discute un peu",
    english: "Let's sit and talk a little",
    words: ["ñu", "toog", "waxtaan", "tuuti"],
    category: 'Loisirs et culture',
    tags: ['conversation', 'social', 'repos']
  },
  {
    original: "Lekool bi dafa neex lool",
    french: "L'école est très agréable",
    english: "School is very pleasant",
    words: ["Lekool", "bi", "dafa", "neex", "lool"],
    category: 'Loisirs et culture',
    tags: ['école', 'adjectif', 'adverbe']
  },
  {
    original: "Xanaa bëgg nga dem lekool bi ?",
    french: "Peut-être veux-tu aller à l'école ?",
    english: "Maybe you want to go to school?",
    words: ["Xanaa", "bëgg", "nga", "dem", "lekool", "bi", "?"],
    category: 'Loisirs et culture',
    tags: ['conjonction', 'école', 'question']
  },
  {
    original: "Toog ci kanam kër bi",
    french: "Assieds-toi devant la maison",
    english: "Sit in front of the house",
    words: ["Toog", "ci", "kanam", "kër", "bi"],
    category: 'Loisirs et culture',
    tags: ['préposition', 'maison', 'localisation']
  },
  {
    original: "Suba, damay dem lekool",
    french: "Demain matin, j'irai à l'école",
    english: "Tomorrow morning I will go to school",
    words: ["Suba", "damay", "dem", "lekool"],
    category: 'Loisirs et culture',
    tags: ['adverbe', 'école', 'futur']
  },
  {
    original: "Bu ngay lekk, noppi rekk",
    french: "Quand tu auras mangé, reste tranquille",
    english: "When you have eaten, stay calm",
    words: ["Bu", "ngay", "lekk", "noppi", "rekk"],
    category: 'Loisirs et culture',
    tags: ['conjonction', 'adverbe', 'action']
  },
  {
    original: "Téere bi dafa baax",
    french: "Le livre est bon",
    english: "The book is good",
    words: ["Téere", "bi", "dafa", "baax"],
    category: 'Loisirs et culture',
    tags: ['livre', 'adjectif']
  },
  {
    original: "Jàng bala ngay nelaw",
    french: "Étudie avant de dormir",
    english: "Study before sleeping",
    words: ["Jàng", "bala", "ngay", "nelaw"],
    category: 'Loisirs et culture',
    tags: ['préposition', 'étude', 'ordre']
  },
  {
    original: "Léegi, damay xool téere bi",
    french: "Maintenant, je lis le livre",
    english: "Now I am reading the book",
    words: ["Léegi", "damay", "xool", "téere", "bi"],
    category: 'Loisirs et culture',
    tags: ['adverbe', 'livre', 'lecture']
  },
  {
    original: "Am na téere bu bees",
    french: "J'ai un nouveau livre",
    english: "I have a new book",
    words: ["Am", "na", "téere", "bu", "bees"],
    category: 'Loisirs et culture',
    tags: ['livre', 'adjectif', 'possession']
  },
  {
    original: "Damay jàng ak sama xarit",
    french: "J'étudie avec mon ami",
    english: "I am studying with my friend",
    words: ["Damay", "jàng", "ak", "sama", "xarit"],
    category: 'Loisirs et culture',
    tags: ['étude', 'conjonction', 'social']
  }
];

// Fonctions utilitaires
export const getSentencesByCategory = (category: keyof typeof SENTENCE_CATEGORIES) =>
  sentencesToConstruct.filter(sentence => sentence.category === category);

export const getSentencesByTags = (tags: string[]) =>
  sentencesToConstruct.filter(sentence => 
    tags.some(tag => sentence.tags.includes(tag))
  );

export const getCategories = () => SENTENCE_CATEGORIES;

export const getAllTags = () => {
  const allTags = new Set<string>();
  sentencesToConstruct.forEach(sentence => {
    sentence.tags.forEach(tag => allTags.add(tag));
  });
  return Array.from(allTags).sort();
};

export const getRandomSentences = (count: number = 10) => {
  const shuffled = [...sentencesToConstruct].sort(() => 0.5 - Math.random());
  return shuffled.slice(0, count);
};

export const getSentencesByDifficulty = (difficulty: 'débutant' | 'intermédiaire' | 'avancé') => {
  const beginnerTags = ['salutations', 'présentation', 'temps', 'localisation', 'réponse'];
  const intermediateTags = ['progressif', 'possessif', 'interrogation', 'négation', 'achat'];
  const advancedTags = ['comparaison', 'conditionnel', 'emphase', 'relatif', 'construction'];
  
  switch (difficulty) {
    case 'débutant':
      return sentencesToConstruct.filter(sentence => 
        sentence.tags.some(tag => beginnerTags.includes(tag))
      );
    case 'intermédiaire':
      return sentencesToConstruct.filter(sentence => 
        sentence.tags.some(tag => intermediateTags.includes(tag))
      );
    case 'avancé':
      return sentencesToConstruct.filter(sentence => 
        sentence.tags.some(tag => advancedTags.includes(tag))
      );
    default:
      return sentencesToConstruct;
  }
};

export const searchSentences = (query: string) => {
  const lowerQuery = query.toLowerCase();
  return sentencesToConstruct.filter(sentence => 
    sentence.original.toLowerCase().includes(lowerQuery) ||
    sentence.french.toLowerCase().includes(lowerQuery) ||
    sentence.words.some(word => word.toLowerCase().includes(lowerQuery)) ||
    sentence.tags.some(tag => tag.toLowerCase().includes(lowerQuery))
  );
};

export default sentencesToConstruct;